/**
 * Security Page
 * Detailed information about Prozpector's security practices
 */

import { MarketingLayout } from "@/components/layout/MarketingLayout";
import { Card } from "@/components/ui/card";
import { SEO } from "@/components/SEO";
import {
  Shield,
  Lock,
  Server,
  Eye,
  Key,
  RefreshCw,
  Database,
  Globe,
  CheckCircle,
  AlertTriangle,
  Zap,
  Users,
  FileCheck,
  Clock
} from "lucide-react";

const securityFeatures = [
  {
    icon: Lock,
    title: "256-bit SSL/TLS Encryption",
    description: "All data transmitted between your browser and our servers is encrypted using industry-standard TLS 1.3 protocols. Your credentials, prospect data, and communications are never exposed.",
    color: "green"
  },
  {
    icon: Shield,
    title: "DDoS & Attack Protection",
    description: "Advanced rate limiting prevents brute force attacks. Our infrastructure includes multiple layers of protection against denial-of-service attacks and malicious traffic.",
    color: "blue"
  },
  {
    icon: Key,
    title: "Secure Authentication",
    description: "JWT-based authentication with secure token handling. Support for multi-factor authentication (MFA) adds an extra layer of protection to your account.",
    color: "purple"
  },
  {
    icon: Database,
    title: "Data Isolation",
    description: "Row-level security ensures complete data isolation between accounts. Your prospect data, sequences, and analytics are never accessible to other users.",
    color: "orange"
  },
  {
    icon: RefreshCw,
    title: "Automatic Backups",
    description: "Continuous database backups with point-in-time recovery. Your data is replicated across multiple availability zones for maximum durability.",
    color: "teal"
  },
  {
    icon: Eye,
    title: "XSS & Injection Prevention",
    description: "All user inputs are sanitized and validated. Content Security Policy (CSP) headers prevent cross-site scripting attacks and code injection.",
    color: "red"
  }
];

const complianceItems = [
  {
    title: "GDPR Compliant",
    description: "Full compliance with EU General Data Protection Regulation. Data processing agreements available upon request.",
    icon: Globe
  },
  {
    title: "SOC 2 Type II Ready",
    description: "Our infrastructure and processes are designed to meet SOC 2 Type II requirements for security, availability, and confidentiality.",
    icon: FileCheck
  },
  {
    title: "Data Portability",
    description: "Export your data anytime in standard formats. You own your data and can take it with you if you leave.",
    icon: Database
  },
  {
    title: "Right to Deletion",
    description: "Request complete deletion of your account and all associated data at any time through our support team.",
    icon: AlertTriangle
  }
];

const infrastructureFeatures = [
  {
    metric: "99.9%",
    label: "Uptime SLA",
    description: "Enterprise-grade availability"
  },
  {
    metric: "24/7",
    label: "Monitoring",
    description: "Continuous security monitoring"
  },
  {
    metric: "<100ms",
    label: "Response Time",
    description: "Fast, responsive API"
  },
  {
    metric: "3+",
    label: "Availability Zones",
    description: "Geographic redundancy"
  }
];

export default function SecurityPage() {
  return (
    <MarketingLayout>
      <SEO
        title="Security | Prozpector"
        description="Learn about Prozpector's enterprise-grade security measures. Bank-level encryption, DDoS protection, GDPR compliance, and more."
        url="https://prozpector.com/security"
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-gray-900 to-black text-white py-20">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 rounded-full mb-6">
              <Shield className="h-10 w-10 text-green-400" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
              Enterprise-Grade Security
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Your data is protected with bank-level encryption and industry-leading security practices.
              We take security seriously so you can focus on growing your business.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6">
              <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>SOC 2 Ready</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>GDPR Compliant</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>256-bit Encryption</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Features Grid */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">How We Protect Your Data</h2>
              <p className="text-xl text-gray-600">
                Multiple layers of security keep your information safe
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {securityFeatures.map((feature, index) => (
                <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                  <div className={`w-12 h-12 bg-${feature.color}-100 rounded-lg flex items-center justify-center mb-4`}>
                    <feature.icon className={`h-6 w-6 text-${feature.color}-600`} />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Infrastructure Stats */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Reliable Infrastructure</h2>
              <p className="text-xl text-gray-600">
                Built on enterprise-grade cloud infrastructure
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              {infrastructureFeatures.map((item, index) => (
                <Card key={index} className="p-6 text-center">
                  <div className="text-4xl font-bold text-primary mb-2">{item.metric}</div>
                  <div className="font-semibold mb-1">{item.label}</div>
                  <div className="text-sm text-gray-600">{item.description}</div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technical Security Details */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Technical Security Measures</h2>
              <p className="text-xl text-gray-600">
                Defense in depth at every layer
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="p-8">
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                  <Zap className="h-6 w-6 text-yellow-500" />
                  Application Security
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Input Validation</strong>
                      <p className="text-sm text-gray-600">All inputs validated with Zod schemas to prevent injection attacks</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Content Security Policy</strong>
                      <p className="text-sm text-gray-600">Strict CSP headers prevent XSS and code injection</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>HTML Sanitization</strong>
                      <p className="text-sm text-gray-600">DOMPurify sanitizes all rendered HTML content</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Rate Limiting</strong>
                      <p className="text-sm text-gray-600">API rate limits prevent abuse and brute force attacks</p>
                    </div>
                  </li>
                </ul>
              </Card>

              <Card className="p-8">
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                  <Server className="h-6 w-6 text-blue-500" />
                  Infrastructure Security
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Encrypted at Rest</strong>
                      <p className="text-sm text-gray-600">All database storage encrypted with AES-256</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Network Isolation</strong>
                      <p className="text-sm text-gray-600">Private VPC with strict security groups</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Webhook Verification</strong>
                      <p className="text-sm text-gray-600">HMAC signatures verify all webhook authenticity</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Secure Headers</strong>
                      <p className="text-sm text-gray-600">X-Frame-Options, HSTS, and other security headers</p>
                    </div>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Compliance & Privacy</h2>
              <p className="text-xl text-gray-600">
                Your rights and our commitments
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {complianceItems.map((item, index) => (
                <Card key={index} className="p-6 flex gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Security Practices */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Our Security Practices</h2>
              <p className="text-xl text-gray-600">
                How we maintain a secure environment
              </p>
            </div>

            <div className="space-y-6">
              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Regular Security Audits</h3>
                    <p className="text-gray-600">
                      We conduct regular security reviews and vulnerability assessments to identify and address potential issues before they become problems.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Employee Security Training</h3>
                    <p className="text-gray-600">
                      All team members receive security awareness training and follow strict access control policies. We operate on a principle of least privilege.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Incident Response</h3>
                    <p className="text-gray-600">
                      We have documented incident response procedures and will notify affected customers within 72 hours of discovering any data breach.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Security Team */}
      <section className="py-20 bg-black text-white">
        <div className="container text-center">
          <Shield className="h-12 w-12 mx-auto mb-6 text-green-400" />
          <h2 className="text-4xl font-bold mb-4">Have Security Questions?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            We're committed to transparency about our security practices. If you have questions or need additional security documentation, our team is here to help.
          </p>
          <a
            href="mailto:security@prozpector.com"
            className="inline-flex items-center gap-2 bg-white text-black px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Contact Security Team
          </a>
          <p className="text-sm text-gray-400 mt-4">
            security@prozpector.com
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
}
